import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import {
  getEmployeeHrProfile,
  upsertEmployeeHrProfile,
} from "@/lib/employeeHrProfiles";
import { toast } from "@/lib/toast";

interface EmployeeEntryDateEditorProps {
  userLogin: string;
  userDisplay: string;
  refreshKey?: number;
  onChanged?: () => void;
}

export function EmployeeEntryDateEditor({
  userLogin,
  userDisplay,
  refreshKey = 0,
  onChanged,
}: EmployeeEntryDateEditorProps) {
  const [entryDate, setEntryDate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const profile = await getEmployeeHrProfile(userLogin);
      setEntryDate(profile.entry_date?.slice(0, 10) ?? "");
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Eintrittsdatum konnte nicht geladen werden.",
      );
    } finally {
      setLoading(false);
    }
  }, [userLogin]);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await upsertEmployeeHrProfile(userLogin, {
        entry_date: entryDate || null,
        user_display: userDisplay,
      });
      toast.success("Eintrittsdatum gespeichert.");
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(
        err instanceof ApiError ? err.message : "Eintrittsdatum konnte nicht gespeichert werden.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-ios border border-separator bg-card p-4 shadow-ios">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-label-tertiary">
        Eintrittsdatum
      </h2>
      <p className="mt-1 text-xs text-label-secondary">
        Ab diesem Tag zählen Soll und Überstunden. Davor wird im Montageplan automatisch
        „Nicht verfügbar“ von 01.01.2020 bis zum Vortag eingetragen.
      </p>
      {loading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="mt-4 flex flex-wrap items-end gap-3">
          <label className="space-y-1">
            <span className="eh-label">Eintritt</span>
            <input
              type="date"
              className="eh-input"
              value={entryDate}
              onChange={(e) => setEntryDate(e.target.value)}
            />
          </label>
          <Button variant="primary" disabled={saving} onClick={() => void handleSave()}>
            Speichern
          </Button>
          {entryDate ? (
            <Button
              variant="ghost"
              disabled={saving}
              onClick={() => {
                setEntryDate("");
              }}
            >
              Leeren
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
